import api from './api';

export interface BusinessInfoPayload {
  name?: string;
  tax_code?: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
  city_id?: string;
  ward_id?: string;
  representative?: string;
  business_type?: string;
  founded_year?: string;
  website?: string;
}

export interface BusinessInfo extends BusinessInfoPayload {
  /** snake_case từ BE — match TenantSettingsExt.business_info. */
}

export interface PaymentShift {
  name: string;
  start_time: string;
  end_time: string;
}

export interface PaymentConfig {
  methods: string[];
  default_method: string;
  shifts: PaymentShift[];
  require_shift_open: boolean;
}

export interface SettingsFull {
  business: BusinessInfo;
  payment: PaymentConfig;
  einvoice: {
    enabled: boolean;
    provider: 'misa' | 'viettel' | 'self';
    auto_issue: boolean;
    default_template?: string | null;
  };
  notifications: {
    email: { enabled: boolean };
    zalo_zns: { enabled: boolean };
    sms: { enabled: boolean };
  };
  integrations: {
    ghtk: { enabled: boolean };
    napas: { enabled: boolean };
  };
  api_keys_enabled: boolean;
}

interface ApiEnvelope<T> {
  data: T;
  success: boolean;
}

// ===== Phase 3 — E-Invoice =====
export interface EInvoiceConfig {
  enabled: boolean;
  provider: 'misa' | 'viettel' | 'self';
  auto_issue: boolean;
  default_template?: string | null;
  has_credentials: boolean;
}

export interface EInvoiceUpdateInput {
  enabled?: boolean;
  provider?: 'misa' | 'viettel' | 'self';
  auto_issue?: boolean;
  default_template?: string;
  /** JSON string của credentials. BE sẽ encrypt server-side. */
  credentials_json?: string;
}

// ===== Phase 3 — Notifications =====
export interface NotificationsConfig {
  email: {
    enabled: boolean;
    smtp_host: string | null;
    smtp_port: number | null;
    smtp_user: string | null;
    from_address: string | null;
    has_password: boolean;
  };
  zalo_zns: {
    enabled: boolean;
    oa_id: string | null;
    has_token: boolean;
  };
  sms: {
    enabled: boolean;
    provider: string | null;
    has_api_key: boolean;
  };
}

export interface NotificationsUpdateInput {
  email?: {
    enabled?: boolean;
    smtp_host?: string;
    smtp_port?: number;
    smtp_user?: string;
    smtp_pass?: string;
    from_address?: string;
  };
  zalo_zns?: {
    enabled?: boolean;
    oa_id?: string;
    access_token?: string;
  };
  sms?: {
    enabled?: boolean;
    provider?: string;
    api_key?: string;
  };
}

// ===== Integrations — GHTK + Napas =====
export interface IntegrationsConfig {
  ghtk: {
    enabled: boolean;
    has_token: boolean;
    has_webhook_token: boolean;
  };
  napas: {
    enabled: boolean;
    client_id: string | null;
    account_prefix: string | null;
    has_public_key: boolean;
    has_private_key: boolean;
  };
}

export interface IntegrationsUpdateInput {
  ghtk?: {
    enabled?: boolean;
    token?: string;
    webhook_token?: string;
  };
  napas?: {
    enabled?: boolean;
    client_id?: string;
    account_prefix?: string;
    public_key?: string;
    private_key?: string;
  };
}

// ===== Phase 4 — API Keys =====
export interface ApiKey {
  _id: string;
  tenant_id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  is_active: boolean;
  last_used_at?: string;
  revoked_at?: string;
  created_by?: string;
  created_at?: string;
}

export interface CreateApiKeyInput {
  name: string;
  permissions?: string[];
}

export interface CreateApiKeyResponse {
  key: string;
  record: ApiKey;
}

// ===== Phase 4 — Audit Log =====
export interface AuditLogEntry {
  _id: string;
  tenant_id: string;
  user_id?: string;
  user_name?: string;
  section: string;
  action: string;
  target_id?: string;
  diff?: Record<string, unknown>;
  ip?: string;
  user_agent?: string;
  created_at: string;
}

export interface AuditLogQueryInput {
  page?: number;
  page_size?: number;
  section?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface AuditLogResponse {
  data: AuditLogEntry[];
  meta: { total: number; page: number; page_size: number };
}

export interface BankAccount {
  _id: string;
  tenant_id: string;
  bank_name: string;
  bank_code?: string;
  account_number: string;
  account_name: string;
  branch?: string;
  enabled?: boolean;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateBankAccountInput {
  bank_name: string;
  bank_code?: string;
  account_number: string;
  account_name: string;
  branch?: string;
  enabled?: boolean;
  is_default?: boolean;
}

export type UpdateBankAccountInput = Partial<
  Omit<CreateBankAccountInput, 'is_default'>
>;

export interface EWallet {
  _id: string;
  tenant_id: string;
  provider: string;
  account_number: string;
  account_name: string;
  enabled?: boolean;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}
export interface CreateEWalletInput {
  provider: string;
  account_number: string;
  account_name: string;
  enabled?: boolean;
  is_default?: boolean;
}
export type UpdateEWalletInput = Partial<Omit<CreateEWalletInput, 'is_default'>>;

export interface CardTerminal {
  _id: string;
  tenant_id: string;
  acquirer: string;
  terminal_id: string;
  account_number?: string;
  account_name: string;
  enabled?: boolean;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}
export interface CreateCardTerminalInput {
  acquirer: string;
  terminal_id: string;
  account_number?: string;
  account_name: string;
  enabled?: boolean;
  is_default?: boolean;
}
export type UpdateCardTerminalInput = Partial<
  Omit<CreateCardTerminalInput, 'is_default'>
>;

export const settingsService = {
  /** GET /v1/settings — composite read. */
  getFull: () =>
    api.get<ApiEnvelope<SettingsFull>>('/settings').then((r) => r.data.data),

  getBusiness: () =>
    api.get<ApiEnvelope<BusinessInfo>>('/settings/business').then((r) => r.data.data),

  updateBusiness: (body: BusinessInfoPayload) =>
    api
      .patch<ApiEnvelope<BusinessInfo>>('/settings/business', body)
      .then((r) => r.data.data),

  getPayment: () =>
    api.get<ApiEnvelope<PaymentConfig>>('/settings/payment').then((r) => r.data.data),

  updatePayment: (body: Partial<PaymentConfig>) =>
    api
      .patch<ApiEnvelope<PaymentConfig>>('/settings/payment', body)
      .then((r) => r.data.data),

  // ===== Phase 2 — Bank accounts =====
  listBankAccounts: () =>
    api
      .get<ApiEnvelope<BankAccount[]>>('/settings/bank-accounts')
      .then((r) => r.data.data),

  createBankAccount: (body: CreateBankAccountInput) =>
    api
      .post<ApiEnvelope<BankAccount>>('/settings/bank-accounts', body)
      .then((r) => r.data.data),

  updateBankAccount: (id: string, body: UpdateBankAccountInput) =>
    api
      .patch<ApiEnvelope<BankAccount>>(`/settings/bank-accounts/${id}`, body)
      .then((r) => r.data.data),

  deleteBankAccount: (id: string) =>
    api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/bank-accounts/${id}`)
      .then((r) => r.data.data),

  setDefaultBankAccount: (id: string) =>
    api
      .post<ApiEnvelope<BankAccount>>(`/settings/bank-accounts/${id}/set-default`)
      .then((r) => r.data.data),

  // ===== E-Wallets =====
  listEWallets: () =>
    api
      .get<ApiEnvelope<EWallet[]>>('/settings/e-wallets')
      .then((r) => r.data.data),

  createEWallet: (body: CreateEWalletInput) =>
    api
      .post<ApiEnvelope<EWallet>>('/settings/e-wallets', body)
      .then((r) => r.data.data),

  updateEWallet: (id: string, body: UpdateEWalletInput) =>
    api
      .patch<ApiEnvelope<EWallet>>(`/settings/e-wallets/${id}`, body)
      .then((r) => r.data.data),

  deleteEWallet: (id: string) =>
    api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/e-wallets/${id}`)
      .then((r) => r.data.data),

  setDefaultEWallet: (id: string) =>
    api
      .post<ApiEnvelope<EWallet>>(`/settings/e-wallets/${id}/set-default`)
      .then((r) => r.data.data),

  // ===== Card Terminals =====
  listCardTerminals: () =>
    api
      .get<ApiEnvelope<CardTerminal[]>>('/settings/card-terminals')
      .then((r) => r.data.data),

  createCardTerminal: (body: CreateCardTerminalInput) =>
    api
      .post<ApiEnvelope<CardTerminal>>('/settings/card-terminals', body)
      .then((r) => r.data.data),

  updateCardTerminal: (id: string, body: UpdateCardTerminalInput) =>
    api
      .patch<ApiEnvelope<CardTerminal>>(`/settings/card-terminals/${id}`, body)
      .then((r) => r.data.data),

  deleteCardTerminal: (id: string) =>
    api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`/settings/card-terminals/${id}`)
      .then((r) => r.data.data),

  setDefaultCardTerminal: (id: string) =>
    api
      .post<ApiEnvelope<CardTerminal>>(
        `/settings/card-terminals/${id}/set-default`,
      )
      .then((r) => r.data.data),

  // ===== Phase 3 — E-Invoice =====
  getEInvoice: () =>
    api.get<ApiEnvelope<EInvoiceConfig>>('/settings/einvoice').then((r) => r.data.data),

  updateEInvoice: (body: EInvoiceUpdateInput) =>
    api
      .patch<ApiEnvelope<EInvoiceConfig>>('/settings/einvoice', body)
      .then((r) => r.data.data),

  // ===== Phase 3 — Notifications =====
  getNotifications: () =>
    api
      .get<ApiEnvelope<NotificationsConfig>>('/settings/notifications')
      .then((r) => r.data.data),

  updateNotifications: (body: NotificationsUpdateInput) =>
    api
      .patch<ApiEnvelope<NotificationsConfig>>('/settings/notifications', body)
      .then((r) => r.data.data),

  // ===== Integrations — GHTK + Napas =====
  getIntegrations: () =>
    api
      .get<ApiEnvelope<IntegrationsConfig>>('/settings/integrations')
      .then((r) => r.data.data),

  updateIntegrations: (body: IntegrationsUpdateInput) =>
    api
      .patch<ApiEnvelope<IntegrationsConfig>>('/settings/integrations', body)
      .then((r) => r.data.data),

  // ===== Phase 4 — API Keys =====
  listApiKeys: () =>
    api.get<ApiEnvelope<ApiKey[]>>('/settings/api-keys').then((r) => r.data.data),

  createApiKey: (body: CreateApiKeyInput) =>
    api
      .post<ApiEnvelope<CreateApiKeyResponse>>('/settings/api-keys', body)
      .then((r) => r.data.data),

  revokeApiKey: (id: string) =>
    api
      .delete<ApiEnvelope<ApiKey>>(`/settings/api-keys/${id}`)
      .then((r) => r.data.data),

  // ===== Phase 4 — Audit Log =====
  listAuditLog: (q: AuditLogQueryInput = {}) =>
    api
      .get<AuditLogResponse>('/settings/audit-log', { params: q })
      .then((r) => r.data),
};
